import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  agentPermissionHooks,
  agentUserTemplateListHooks,
  agentUserVoiceListHooks,
  agentUserMessagesHooks,
  agentsHooks,
  subscriptionHook,
} from "@/manager";
import { AgentVideoModal } from "@/components/agent-modal-video";
import { AgentMomentModal } from "@/components/agent-modal-monent";
import { AgentPostModal } from "@/components/agent-modal-post";
import { AgentAtricleModal } from "@/components/agent-modal-atricle";
import { PanelAgentRecords } from "@/components/agent-panel-records";
import { PanelAgentDetail } from "@/components/agent-panel-detail";
import { PanelAgentEdit } from "@/components/agent-panel-edit";
import { PanelAgentSubscriptionSettings } from "@/components/agent-panel-subscription-settings";
import { CreationHeader } from "./creation-header";
import { Content, VoiceItem } from "./content";
import { ContentInput, INPUT_TYPE_MAP } from "./content-input";
import { UnsubscribedImage } from "./assets/unsubscribed-icon";
import { InputMode, TemplateItem } from "./types";
import { Tooltip } from "../ui/modal";
import { MonitorIcon } from "./assets/monitor-icon";
import { INPUT_TYPE, TASK_STATUS, type ScriptReplaceMark } from "@/manager/manager/agent-user-messages-manager";
import { useToast } from "@/hooks/use-toast";
import { AgentAiCreationVideoModal } from "../agent-modal-ai-creation-video";
import AgentAiCreationImageModal from "../agent-modal-ai-creation-image";
import { getModelOptionByValue, getModelTypeByValue } from "./constants/ai-creation-model";
import { ConfirmModal } from '../common/confirm-modal';
import { MessageItemData } from "@/services/push-info-origin-data-record-list";
import {
  createEstimatedDurationStateFromSeconds,
  getEstimatedDurationSeconds,
} from "./estimated-video-duration";

// 类型对应字段映射
const typeKeyMap = {
  1: "videoSubscribe",
  2: "blogSubscribe",
  3: "momentsSubscribe",
  4: "graphicSubscribe",
  5: "articleSubscribe",
  6: "aiSubscribe",
}

/** Agent 创作页 */
export const AgentCreation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { toast } = useToast();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [scrollToken, setScrollToken] = useState(0);
  const [showTemplates, setShowTemplates] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [aiCreationVideoOpen, setAiCreationVideoOpen] = useState(false);
  const [aiCreationImageOpen, setAiCreationImageOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    videoUrl: string;
    poster?: string;
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
  } | null>(null);
  const [selectedAiCreationVideo, setSelectedAiCreationVideo] = useState<{
    videoUrl: string;
    poster?: string;
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
    aspectRatio?: string;
    model?: string;
    duration?: number;
    imageSize?: string;
    inputAddress?: string;
  } | null>(null);
  const [selectAiCreationImage, setSelectAiCreationImage] = useState<{
    imageUrls: string[];
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
    aspectRatio?: string;
    model?: string;
    imageSize?: string;
    inputAddress?: string;
    currentIndex?: number;
  } | null>(null);
  const [momentModalOpen, setMomentModalOpen] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState<{
    images: string[];
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
  } | null>(null);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<{
    images: string[];
    title: string;
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
  } | null>(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<{
    title: string;
    contentHtml: string;
    image?: string;
    publishedAt?: number;
    summary?: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
  } | null>(null);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [currentType, setCurrentType] = useState<
    "video" | "podcast" | "moments" | "post" | "article" | ""
  >("");
  const [isException, setIsException] = useState(false);

  // 刷新用户权限信息
  agentPermissionHooks.useInitialPermissionDetail();

  const {
    loading,
    loadingMore,
    groups,
    generate,
    cancelGenerate,
    canceling,
    loadMore,
    deleteLoading,
    deleteCreationOneofRecordLoading,

    deleteMessage,
    deleteCreationOneofRecord,
    removeMessageById,
    generateAiCreationVideo,
    generateAiCreationImage,
  } = agentUserMessagesHooks.useAgentUserMessages({ agentId: id });

  const { scriptMarks, setScriptMarks, clearScriptMarks } =
    agentUserMessagesHooks.useLiveScriptPronunciationMarks();

  // 获取短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数
  subscriptionHook.useFeatureEstimateAll();

  const { subscriptionState, isPlatformAdmin, userPermissionDetail } =
    agentPermissionHooks.useUserPermission();
  const { agent, loading: agentLoading, introductionTemplate } = agentsHooks.useCurrentAgent();
  const { templates: videoTemplates, refresh: refreshVideoTemplates } =
    agentUserTemplateListHooks.useTemplateListData({
      agentId: id,
      type: "video",
    });
  const { templates: voiceTemplates, loading: voiceTemplateLoading, saveVoiceTemplate, removeVoiceTemplate, saveLoading: saveVoiceLoading } =
    agentUserVoiceListHooks.useVoiceListData({
      agentId: id,
      type: "podcast",
    });
  const { templates: momentTemplates } =
    agentUserTemplateListHooks.useTemplateListData({
      agentId: id,
      type: "moments",
    });
  const { templates: postTemplates } =
    agentUserTemplateListHooks.useTemplateListData({
      agentId: id,
      type: "post",
    });
  const { templates: articleTemplates } =
    agentUserTemplateListHooks.useTemplateListData({
      agentId: id,
      type: "article",
    });
  // ai创作相关
  const {
    aiCreationType,
    setAiCreationType,
    aiCreationModel,
    setAiCreationModel,
    aiCreationParams,
    setAiCreationParams,
    referenceImages,
    setReferenceImages,
    uploadReferenceImages,
    uploadReferenceImagesLoading,
    endFrameImage,
    endFrameImageLoading,
    handleUploadEndFrameImage,
    handleEndFrameImageChange,
    handleSwitchFirstAndEndFrame,
  } = agentUserMessagesHooks.useAiCreationData();
  const [mode, setMode] = useState<InputMode>(InputMode.voice);
  const [inputText, setInputText] = useState('');
  const [inputType, setInputType] = useState<number>(INPUT_TYPE.VIDEO);
  const [linkUrl, setLinkUrl] = useState('');
  const [noteId, setNoteId] = useState<number | null>(null);
  const [noteName, setNoteName] = useState('');

  // 输出相关配置
  const {
    // 输出语言
    outLang,
    setOutLang,
    // 短视频预估时长
    estimatedDuration,
    setEstimatedDuration,
    // 口播台词开关
    liveStreamScript,
    setLiveStreamScript,
    // 脚本违禁词检测开关
    openScriptIllegal,
    setOpenScriptIllegal,
  } = agentUserMessagesHooks.useOutputConfig(id);

  // 违规词检测相关
  const {
    scriptRiskWords,
    checkingScriptIllegal,
    checkScriptIllegal,
    clearScriptRiskWords,
    isScriptIllegalCheckFreshForText,
  } = agentUserMessagesHooks.useAudioContentIllegalCheck();

  // 试听语音相关
  const {
    loading: isAudioGenerating,
    generateAudio,
    audioUrl: managedAudioUrl,
    clearTTSCreateResult,
  } = agentUserMessagesHooks.useTTSAudition();

  useEffect(() => {
    if (!openScriptIllegal) clearScriptRiskWords();
  }, [openScriptIllegal, clearScriptRiskWords]);

  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState<TemplateItem | undefined>();
  const [selectedVoiceTemplate, setSelectedVoiceTemplate] = useState<VoiceItem | undefined>();
  const [selectedMomentTemplate, setSelectedMomentTemplate] = useState<TemplateItem | undefined>();
  const [selectedPostTemplate, setSelectedPostTemplate] = useState<TemplateItem | undefined>();
  const [selectedArticleTemplate, setSelectedArticleTemplate] = useState<TemplateItem | undefined>();

  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [selectedCancelMessage, setSelectedCancelMessage] = useState<{ taskId: string; type: number } | undefined>(undefined);

  useEffect(() => {
    if (!location.state) return;
    // 处理从知识库跳转过来的参数，设置 noteId 和 noteName
    if (location.state?.noteId) {
      setNoteId(location.state.noteId)
      setNoteName(location.state.noteName || t('assets.knowledge_item.untitled_note'))
      setInputType(INPUT_TYPE.VIDEO)
    }
  }, [location.state]);

  useEffect(() => {
    // 口播模式下，不允许有附件和链接
    if (liveStreamScript && [1, 2].includes(inputType)) {
      setNoteName(null)
      setNoteId(null)
      setLinkUrl(null)
    }
  }, [liveStreamScript, inputType]);

  useEffect(() => {
    if (containerRef.current) {
      setMounted(true);
    }
  }, [subscriptionState.subscribed, userPermissionDetail?.roleStatus]);

  const message = groups?.flatMap((g) => {
    const groupKey = `${g.date} ${g.time} ${g.type}`;
    return g.messages.map((msg) => ({
      ...msg,
      groupKey,
    }));
  });

  // 判断异常状态
  useEffect(() => {
    if (loading || agentLoading) return;

    const noSubscription = [
      agent?.subscribeInfo?.videoSubscribe,
      agent?.subscribeInfo?.blogSubscribe,
      agent?.subscribeInfo?.momentsSubscribe,
      agent?.subscribeInfo?.graphicSubscribe,
      agent?.subscribeInfo?.articleSubscribe
    ].every((item) => item === 0)

    setIsException(subscriptionState.isException || noSubscription);

  }, [subscriptionState.isException, agent?.subscribeInfo, loading, agentLoading]);

  // 重置异常状态
  useEffect(() => {
    setIsException(false);
    clearEditMessageState();
  }, [location.pathname]);

  // 判断是否有内容类型生成权限
  const hasGeneratePermission = (type: number) => {
    return Boolean(agent?.subscribeInfo?.[typeKeyMap[type]])
  }

  // 重新生成消息(调生成接口)
  const handleRegenerateMessage = async (taskId: string, externalMessage?: MessageItemData) => {
    // 找到对应消息
    const message = externalMessage || groups?.flatMap((g) => g.messages).find((msg) => msg.taskId === taskId);
    const parseData = JSON.parse(message?.reqBody || '{}');
    if (!message) return;
    // 消息类型
    const type = Number(parseData.type) || message?.type;
    // 检查是否有权限
    const allow = hasGeneratePermission(type);

    if (!allow) {
      toast({
        description: t('agent_creation.no_permission_generate'),
      })
      return;
    }

    setScrollToken((s) => s + 1);
    setRecordsOpen(false);

    console.log('edit:', { message, parseData })

    // ==== ai创作 ====
    if (type === INPUT_TYPE.AI_CREATION) {
      const model = parseData.model || parseData.modelId;
      if (!model) return;

      const creationType = getModelTypeByValue(model);
      if (!creationType) return;

      const { text, type, level, duration, fileUrl, fileUrl1, fileUrls, aspectRatio, resolution, imageSize, count } = parseData;

      if (creationType === 'video') {
        await handleGenerateAiCreationVideo({
          text,
          type: type || 6,
          model,
          duration: Number(duration),
          aspectRatio,
          fileUrl,
          fileUrl1,
          imageSize: imageSize || resolution,
        })
        return;
      } else if (creationType === 'image') {
        await handleGenerateAiCreationImage({
          text,
          type: type || 6,
          model,
          imageSize: imageSize || resolution,
          count: Number(count),
          aspectRatio,
          fileUrls,
        })
        return;
      }
    }

    // ==== 其他类型 ====
    const params = {
      text: parseData.text,
      type: parseData.type,
      templateId: parseData.template_id,
      voiceId: parseData?.audio_id,
      linkUrl: undefined,
      noteId: undefined,
      noteName: undefined,
      templateResource: undefined,
      outLang: outLang,
      typeScene: parseData.type_scene,
      marks: Array.isArray(parseData.marks) && parseData.marks.length ? parseData.marks : undefined,
      // point: parseData.point,
      resultVideoDuration: parseData.result_video_duration ? Number(parseData.result_video_duration) : undefined,
    }
    if (parseData.link_url) {
      params.linkUrl = parseData.link_url;
    }
    if (parseData.note_id) {
      params.noteId = parseData.note_id;
      params.noteName = message?.noteContentTitle || t('assets.knowledge_item.untitled_note');
    }
    if (parseData.template_resource) {
      params.templateResource = parseData.template_resource;
    }

    await generate(params);
    setScrollToken((s) => s + 1);
  }

  // 清空编辑消息状态
  const clearEditMessageState = () => {
    clearTTSCreateResult(); // 清空试听语音结果
    clearScriptRiskWords(); // 清空口播违禁词检测结果
    setScriptMarks([]); // 清口播台词发音修改标记
  }

  // 编辑消息
  const handleEditMessage = (taskId: string) => {
    if (!validBeforeEdit()) return;

    // 清空状态
    clearEditMessageState();

    // 找到对应消息
    const message = groups?.flatMap((g) => g.messages).find((msg) => msg.taskId === taskId);
    const parseData = JSON.parse(message?.reqBody || '{}');

    // 消息类型
    const type = Number(parseData.type) || message.type;

    // 检查是否有权限
    const allow = hasGeneratePermission(type);
    if (!allow) {
      toast({
        description: t('agent_creation.no_permission_generate'),
      })
      return;
    }
    console.log('edit:', { message, parseData })
    setInputText(parseData.text || message.inputText);
    setInputType(type);
    setOutLang(parseData.out_lang || outLang);
    setLiveStreamScript(!!parseData.type_scene);

    // 设置短视频预估时长（后端返回的是总秒数，例如 255）
    if (parseData.result_video_duration) {
      setEstimatedDuration(
        createEstimatedDurationStateFromSeconds(Number(parseData.result_video_duration)),
      );
    }

    // 设置口播台词发音修改标记
    const rawMarks = parseData.marks;
    if (parseData.type_scene === "speech" && rawMarks && rawMarks?.length) {
      setScriptMarks(rawMarks);
    } else {
      setScriptMarks([]);
    }

    // 处理 ai 创作
    if (type === INPUT_TYPE.AI_CREATION) {
      const model = parseData.model || parseData.modelId;
      if (!model) return;

      const creationType = getModelTypeByValue(model);
      if (!creationType) return;

      const { text, type, fileUrl, fileUrl1, fileUrls, level, duration, aspectRatio, imageSize, resolution, count } = parseData;

      setAiCreationType(creationType as 'video' | 'image');
      setAiCreationModel(model);

      if (fileUrl) {
        setReferenceImages([fileUrl]);
      } else if (fileUrls?.length) {
        setReferenceImages(fileUrls);
      } else {
        setReferenceImages([]);
      }

      if (fileUrl1) {
        handleEndFrameImageChange([fileUrl1]);
      } else {
        handleEndFrameImageChange([]);
      }

      setAiCreationParams({
        duration: duration || aiCreationParams.duration,
        aspectRatio: aspectRatio || aiCreationParams.aspectRatio,
        imageSize: imageSize || resolution || aiCreationParams.imageSize,
        count: count || aiCreationParams.count,
      });
      setShowTemplates(true);
      return;
    }

    // 处理其他类型
    // 支持链接输入，才设置 linkUrl
    if (agent?.applyInputTypeInfo?.[INPUT_TYPE_MAP[type]]?.split(',')?.includes('2')) {
      setLinkUrl(parseData.link_url || '');
    }

    // 支持文档输入，才设置 noteId 和 noteName
    if (agent?.applyInputTypeInfo?.[INPUT_TYPE_MAP[type]]?.split(',')?.includes('3')) {
      setNoteId(parseData.note_id || null);
      if (parseData.note_id) {
        setNoteName(parseData.note_content_title || message.noteContentTitle || t('assets.knowledge_item.untitled_note'));
      }
    }

    if (type === 1) {
      setCurrentType('video');
      const temp = videoTemplates?.find((t) => t.id === parseData.template_id)
      temp && setSelectedVideoTemplate(temp);
    } else if (type === 2) {
      setCurrentType('podcast');
      const temp = voiceTemplates?.find((t) => t.id === parseData.template_id)
      temp && setSelectedVoiceTemplate(temp);
    } else if (type === 3) {
      setCurrentType('moments');
      const temp = momentTemplates?.find((t) => t.id === parseData.template_id)
      temp && setSelectedMomentTemplate(temp);
    } else if (type === 4) {
      setCurrentType('post');
      const temp = postTemplates?.find((t) => t.id === parseData.template_id)
      temp && setSelectedPostTemplate(temp);
    } else if (type === 5) {
      setCurrentType('article');
      const temp = articleTemplates?.find((t) => t.id === parseData.template_id)
      temp && setSelectedArticleTemplate(temp);
    }
    setShowTemplates(true);
  }

  // 编辑前校验
  const validBeforeEdit = () => {
    if (isAudioGenerating) {
      toast({ description: t('live_script.audio_generating') })
      return false;
    }
    if (checkingScriptIllegal) {
      toast({ description: t('illegal_word.script_illegal_checking') })
      return false;
    }
    return true;
  }

  useEffect(() => {
    if (id) {
      setInputText('');
      clearScriptMarks();
    }
  }, [id, clearScriptMarks]);

  // 提交任务
  const handleSubmit = async ({
    content,
    inputType,
    templateId,
    voiceId,
    linkUrl,
    noteId,
    noteName,
    templateResource,
    scriptMarks: submitMarks,
    point,
  }) => {
    setScrollToken((s) => s + 1);
    try {
      if (inputType === INPUT_TYPE.AI_CREATION) {
        if (aiCreationType === 'video') {
          await handleGenerateAiCreationVideo({
            text: content,
            type: inputType,
            model: aiCreationModel,
            duration: Number(aiCreationParams.duration),
            aspectRatio: aiCreationParams.aspectRatio,
            imageSize: aiCreationParams.imageSize,
            fileUrl: referenceImages[0],
            fileUrl1: endFrameImage[0],
          })
        } else {
          await handleGenerateAiCreationImage({
            text: content,
            type: inputType,
            model: aiCreationModel,
            imageSize: aiCreationParams.imageSize,
            count: Number(aiCreationParams.count),
            aspectRatio: aiCreationParams.aspectRatio,
            fileUrls: referenceImages,
          })
        }
      } else {
        // 是否为短视频、播客的口播模式
        const isLiveForInputType = [1, 2].includes(inputType) && liveStreamScript;
        console.log('estimatedDuration:', estimatedDuration.preset)
        await generate({
          text: content,
          type: inputType,
          templateId,
          voiceId,
          templateResource,
          outLang: !isLiveForInputType ? outLang : null, // 非口播模式，传 outLang
          linkUrl: !isLiveForInputType ? linkUrl : null,
          noteId: !isLiveForInputType ? noteId : null,
          noteName: !isLiveForInputType ? noteName : null,
          typeScene: isLiveForInputType ? 'speech' : '',
          marks: isLiveForInputType && submitMarks?.length ? submitMarks : undefined,
          point,
          // 仅在短视频普通模式传递预估时长
          resultVideoDuration: inputType === INPUT_TYPE.VIDEO && !isLiveForInputType ? getEstimatedDurationSeconds(estimatedDuration) : null,
        });
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
    setScrollToken((s) => s + 1);
  }

  // Ai创作，视频生成
  const handleGenerateAiCreationVideo = async (params: {
    text: string,
    type: number,
    model: string,
    duration: number,
    aspectRatio: string,
    imageSize?: string,
    fileUrl: string,
    fileUrl1: string,
  }) => {
    try {
      await generateAiCreationVideo({
        ...params,
        type: Number(params.type),
      })
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // Ai创作，图片生成
  const handleGenerateAiCreationImage = async (params: {
    text: string,
    type: number,
    model: string,
    imageSize: string,
    count: number,
    aspectRatio: string,
    fileUrls: string[],
  }) => {
    try {
      await generateAiCreationImage(params)
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // 取消任务
  const handleCancel = async () => {
    try {
      // 取消生成
      const { taskId, type } = selectedCancelMessage || {};
      if (!taskId || !type) return;
      await cancelGenerate(taskId, type);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // 用做参考图
  const handleUseReferenceImage = (url: string) => {
    const modelConfig = getModelOptionByValue(aiCreationModel);
    if (referenceImages.length >= modelConfig?.maxImages) {
      toast.default({
        description: t('ai_creation.use_for_reference_image_tips'),
      });
    } else {
      setReferenceImages([...referenceImages, url]);
      // 用做参考图，同步设置为Ai创作
      setInputType(INPUT_TYPE.AI_CREATION);
    }
    setAiCreationImageOpen(false);
    setSelectAiCreationImage(null);
    setShowTemplates(true);
  }

  console.log('subscriptionState', { subscriptionState })
  return (
    <div
      className="h-screen flex-1 flex flex-col overflow-hidden relative bg-[rgba(249,250,251)]"
      ref={containerRef}
    >
      <CreationHeader
        operationPlatformButton={
          isPlatformAdmin ? <OperationPlatformButton /> : null
        }
        onOpenRecords={() => setRecordsOpen(true)}
        onOpenDetail={() => setDetailOpen(true)}
      />
      <Content
        isExpired={userPermissionDetail?.orderUser?.alreadyExpired}
        expiredAt={userPermissionDetail?.orderUser?.expireAt}
        isException={isException}
        isLoading={loading}
        isLoadingMore={loadingMore}
        scrollToBottomToken={scrollToken}
        messages={message}
        deleteLoading={deleteLoading}
        onDeleteMessage={async (id) => {
          await deleteMessage(id);
        }}
        deleteCreationOneofRecordLoading={deleteCreationOneofRecordLoading}
        onDeleteCreationOneofRecord={async (params) => {
          await deleteCreationOneofRecord(params);
        }}
        onGenerateMessage={async (id) => {
          await handleRegenerateMessage(id);
        }}
        onEditMessage={handleEditMessage}
        onOpenVideo={(payload) => {
          setSelectedVideo(payload);
          setVideoModalOpen(true);
        }}
        onOpenAiCreationVideo={(payload) => {
          setSelectedAiCreationVideo(payload);
          setAiCreationVideoOpen(true);
        }}
        onOpenAiCreationImage={(payload) => {
          setSelectAiCreationImage(payload);
          setAiCreationImageOpen(true);
        }}
        onOpenMoment={(payload) => {
          setSelectedMoment(payload);
          setMomentModalOpen(true);
        }}
        onOpenPost={(payload) => {
          setSelectedPost(payload);
          setPostModalOpen(true);
        }}
        onOpenArticle={(payload) => {
          setSelectedArticle(payload);
          setArticleModalOpen(true);
        }}
        onScrollTemplatesChange={(show) => setShowTemplates(show)}
        onLoad={async () => {
          await loadMore();
        }}
        onUseReferenceImage={(url) => handleUseReferenceImage(url)}
        onCancel={(params) => {
          setSelectedCancelMessage(params);
          setShowCancelConfirmModal(true);
        }}
      />
      <div>
        <ContentInput
          isException={isException}
          isExpired={userPermissionDetail?.orderUser?.alreadyExpired}
          expiredAt={userPermissionDetail?.orderUser?.expireAt}
          applyInputTypeInfo={agent?.applyInputTypeInfo}
          showVideo={Boolean(agent?.subscribeInfo?.videoSubscribe)}
          showPodcast={Boolean(agent?.subscribeInfo?.blogSubscribe)}
          showMoments={Boolean(agent?.subscribeInfo?.momentsSubscribe)}
          showPost={Boolean(agent?.subscribeInfo?.graphicSubscribe)}
          showArticle={Boolean(agent?.subscribeInfo?.articleSubscribe)}
          showAiCreation={Boolean(agent?.subscribeInfo?.aiSubscribe)}
          videoTemplates={videoTemplates}
          voiceTemplates={voiceTemplates}
          momentTemplates={momentTemplates}
          postTemplates={postTemplates}
          articleTemplates={articleTemplates}
          showTemplates={showTemplates}
          onShowTemplatesChange={setShowTemplates}
          onSubmit={handleSubmit}
          mode={mode}
          inputText={inputText}
          inputType={inputType}
          outLang={outLang}
          estimatedDuration={estimatedDuration}
          linkUrl={linkUrl}
          noteId={noteId}
          noteName={noteName}
          liveStreamScript={liveStreamScript}
          openScriptIllegal={openScriptIllegal}
          scriptRiskWords={scriptRiskWords}
          scriptIllegalChecking={checkingScriptIllegal}
          isAudioGenerating={isAudioGenerating}
          onGenerateAudio={generateAudio}
          managedAudioUrl={managedAudioUrl}
          saveVoiceTemplateLoading={saveVoiceLoading}
          voiceTemplateLoading={voiceTemplateLoading}
          aiCreationType={aiCreationType}
          aiCreationModel={aiCreationModel}
          aiCreationParams={aiCreationParams}
          referenceImages={referenceImages}
          uploadReferenceImagesLoading={uploadReferenceImagesLoading}
          selectedVideoTemplate={selectedVideoTemplate}
          selectedVoiceTemplate={selectedVoiceTemplate}
          selectedMomentTemplate={selectedMomentTemplate}
          selectedPostTemplate={selectedPostTemplate}
          selectedArticleTemplate={selectedArticleTemplate}
          endFrameImage={endFrameImage}
          endFrameImageLoading={endFrameImageLoading}
          onRefreshVideoTemplates={refreshVideoTemplates}
          onUploadEndFrameImage={handleUploadEndFrameImage}
          onEndFrameImageChange={handleEndFrameImageChange}
          onSwitchFirstAndEndFrame={handleSwitchFirstAndEndFrame}
          onModeChange={setMode}
          onInputTextChange={setInputText}
          onInputTypeChange={setInputType}
          onLiveStreamScriptChange={setLiveStreamScript}
          onOpenScriptIllegalChange={setOpenScriptIllegal}
          onOutLangChange={setOutLang}
          onEstimatedDurationChange={setEstimatedDuration}
          onLinkUrlChange={setLinkUrl}
          onNoteIdChange={setNoteId}
          onNoteNameChange={setNoteName}
          onAiCreationTypeChange={setAiCreationType}
          onAiCreationModelChange={setAiCreationModel}
          onAiCreationParamsChange={setAiCreationParams}
          onReferenceImagesChange={setReferenceImages}
          onSelectedVideoTemplateChange={setSelectedVideoTemplate}
          onSelectedVoiceTemplateChange={setSelectedVoiceTemplate}
          onSelectedMomentTemplateChange={setSelectedMomentTemplate}
          onSelectedPostTemplateChange={setSelectedPostTemplate}
          onUploadReferenceImages={uploadReferenceImages}
          onSaveVoiceTemplate={saveVoiceTemplate}
          onRemoveVoiceTemplate={removeVoiceTemplate}
          onSelectedArticleTemplateChange={(article) => {
            setSelectedArticleTemplate(article);
          }}
          scriptMarks={scriptMarks}
          onScriptMarksChange={setScriptMarks}
          onCheckScriptIllegal={checkScriptIllegal}
          onIsScriptIllegalCheckFreshForText={isScriptIllegalCheckFreshForText}
          onClearScriptRiskWords={clearScriptRiskWords}
          onClearTTSCreateResult={clearTTSCreateResult}
        />
      </div>
      {selectedVideo && (
        <AgentVideoModal
          open={videoModalOpen}
          onOpenChange={(open) => {
            setVideoModalOpen(open);
            if (!open) setSelectedVideo(null);
          }}
          videoUrl={selectedVideo.videoUrl}
          poster={selectedVideo.poster}
          avatarUrl={agent?.subAvatar || ""}
          nickname={agent?.subName || ""}
          text={selectedVideo.text}
          deleteLoading={deleteLoading}
          isAutoPush={selectedVideo?.autoPush}
          onReedit={() => handleEditMessage(selectedVideo?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectedVideo?.taskId)}
          onDelete={async () => await deleteMessage(selectedVideo?.id)}
        />
      )}
      {selectedAiCreationVideo && (
        <AgentAiCreationVideoModal
          open={aiCreationVideoOpen}
          onOpenChange={(open) => {
            setAiCreationVideoOpen(open);
            if (!open) setSelectedAiCreationVideo(null);
          }}
          videoUrl={selectedAiCreationVideo.videoUrl}
          poster={selectedAiCreationVideo.poster}
          text={selectedAiCreationVideo.text}
          deleteLoading={deleteLoading}
          isAutoPush={selectedAiCreationVideo?.autoPush}
          aspectRatio={selectedAiCreationVideo?.aspectRatio}
          model={selectedAiCreationVideo?.model}
          imageSize={selectedAiCreationVideo?.imageSize}
          duration={selectedAiCreationVideo?.duration}
          inputAddress={selectedAiCreationVideo?.inputAddress}
          onReedit={() => handleEditMessage(selectedAiCreationVideo?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectedAiCreationVideo?.taskId)}
          onDelete={async () => await deleteMessage(selectedAiCreationVideo?.id)}
        />
      )}
      {selectAiCreationImage && (
        <AgentAiCreationImageModal
          open={aiCreationImageOpen}
          onOpenChange={(open) => {
            setAiCreationImageOpen(open);
            if (!open) setSelectAiCreationImage(null);
          }}
          imageUrls={selectAiCreationImage.imageUrls}
          text={selectAiCreationImage.text}
          deleteLoading={deleteLoading}
          isAutoPush={selectAiCreationImage?.autoPush}
          aspectRatio={selectAiCreationImage?.aspectRatio}
          model={selectAiCreationImage?.model}
          imageSize={selectAiCreationImage?.imageSize}
          inputAddress={selectAiCreationImage?.inputAddress}
          currentIndex={selectAiCreationImage?.currentIndex}
          onReedit={() => handleEditMessage(selectAiCreationImage?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectAiCreationImage?.taskId)}
          onDelete={async () => await deleteMessage(selectAiCreationImage?.id)}
          onUseReferenceImage={handleUseReferenceImage}
        />
      )}
      {selectedMoment && (
        <AgentMomentModal
          open={momentModalOpen}
          onOpenChange={(open) => {
            setMomentModalOpen(open);
            if (!open) setSelectedMoment(null);
          }}
          avatarUrl={agent?.subAvatar || ""}
          nickname={agent?.subName || ""}
          text={selectedMoment.text}
          images={selectedMoment.images}
          deleteLoading={deleteLoading}
          isAutoPush={selectedMoment?.autoPush}
          onReedit={() => handleEditMessage(selectedMoment?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectedMoment?.taskId)}
          onDelete={async () => await deleteMessage(selectedMoment?.id)}
        />
      )}

      {selectedPost && (
        <AgentPostModal
          open={postModalOpen}
          onOpenChange={(open) => {
            setPostModalOpen(open);
            if (!open) setSelectedPost(null);
          }}
          avatarUrl={agent?.subAvatar || ""}
          nickname={agent?.subName || ""}
          title={selectedPost.title}
          text={selectedPost.text}
          images={selectedPost.images}
          deleteLoading={deleteLoading}
          isAutoPush={selectedPost?.autoPush}
          onReedit={() => handleEditMessage(selectedPost?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectedPost?.taskId)}
          onDelete={async () => await deleteMessage(selectedPost?.id)}
        />
      )}

      {selectedArticle && (
        <AgentAtricleModal
          open={articleModalOpen}
          onOpenChange={(open) => {
            setArticleModalOpen(open);
            if (!open) setSelectedArticle(null);
          }}
          title={selectedArticle.title}
          quote={selectedArticle.summary}
          contentHtml={selectedArticle.contentHtml}
          avatarUrl={agent?.subAvatar || ""}
          nickname={agent?.subName || ""}
          publishedAt={selectedArticle.publishedAt}
          imageUrl={selectedArticle.image}
          deleteLoading={deleteLoading}
          isAutoPush={selectedArticle?.autoPush}
          onReedit={() => handleEditMessage(selectedArticle?.taskId)}
          onRegenerate={() => handleRegenerateMessage(selectedArticle?.taskId)}
          onDelete={async () => await deleteMessage(selectedArticle?.id)}
        />
      )}

      {recordsOpen && (
        <PanelAgentRecords
          isExpired={userPermissionDetail?.orderUser?.alreadyExpired}
          expiredAt={userPermissionDetail?.orderUser?.expireAt}
          subscribeInfo={agent?.subscribeInfo}
          agentId={agent?.id}
          open={recordsOpen}
          onOpenChange={setRecordsOpen}
          onDeleteMessage={removeMessageById}
          onReGenerateMessage={(taskId, message) => handleRegenerateMessage(taskId, message)}
          onReedit={(taskId) => handleEditMessage(taskId)}
        />
      )}
      {(
        <PanelAgentDetail
          open={detailOpen}
          agent={agent}
          onOpenChange={setDetailOpen}
          expireAt={userPermissionDetail?.orderUser?.expireAt}
          videoSubscribe={Boolean(agent?.subscribeInfo?.videoSubscribe)}
          podcastSubscribe={Boolean(agent?.subscribeInfo?.blogSubscribe)}
          momentsSubscribe={Boolean(agent?.subscribeInfo?.momentsSubscribe)}
          graphicSubscribe={Boolean(agent?.subscribeInfo?.graphicSubscribe)}
          articleSubscribe={Boolean(agent?.subscribeInfo?.articleSubscribe)}
          onNavigate={async (key) => {
            switch (key) {
              case "personal":
                setEditOpen(true);
                return;
              case "video":
              case "podcast":
              case "moments":
              case "post":
              case "article": {
                setCurrentType(key);
                setSubscriptionOpen(true);
                return;
              }
            }
          }}
        />
      )}
      {editOpen && (
        <PanelAgentEdit
          open={editOpen}
          onOpenChange={setEditOpen}
          name={agent?.subName || ""}
          intro={agent?.subIntro || ""}
          avatar={agent?.subAvatar || ""}
          introductionTemplate={introductionTemplate || ""}
          introduction={agent?.subIntroduction || ""}
          agentId={agent?.id}
        />
      )}
      {agent && currentType && subscriptionOpen && (
        <PanelAgentSubscriptionSettings
          open={subscriptionOpen}
          agentId={agent.id}
          type={currentType}
          onOpenChange={setSubscriptionOpen}
          pushSettings={agent?.pushSettings}
        />
      )}
      {showCancelConfirmModal && (
        <ConfirmModal
          open={showCancelConfirmModal}
          onOpenChange={(open) => {
            setShowCancelConfirmModal(open);
            if (!open) setSelectedCancelMessage(null);
          }}
          title={t('agent_creation.content.cancel_title')}
          description={t('agent_creation.content.cancel_desc')}
          cancel={t('agent_creation.content.cancel')}
          confirm={t('agent_creation.content.confirm')}
          onConfirm={handleCancel}
          loading={canceling}
        />
      )}
      {!subscriptionState.subscribed &&
        mounted &&
        containerRef.current &&
        createPortal(
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-white/50 backdrop-blur-sm z-50 flex-col gap-4">
            <UnsubscribedImage className="w-[16rem] h-[10rem]" />
            <div className="text-2xl font-semibold text-[rgba(16,24,40,1)]">
              {t("agent_creation.unsubscribed.title")}
            </div>
            <div className="text-sm text-[rgba(102,112,133,1)]">
              {t("agent_creation.unsubscribed.description")}
            </div>
            <button
              className="flex justify-center items-center gap-2 px-4 py-2.5 text-white text-medium bg-i-normal shadow-[0px_1px_2px_rgba(16,24,40,0.05)] rounded-sm hover:bg-[rgba(40,47,62,1)] transition-all duration-200 h-10 text-sm font-semibold"
              onClick={() => {
                window.open("mailto:support@bizdesk.ai");
              }}
            >
              {t("agent_creation.unsubscribed.contact")}
            </button>
            {isPlatformAdmin ? <OperationPlatformButton className='absolute top-8 right-8' /> : null}
          </div>,
          containerRef.current
        )}
    </div>
  );
};

/** 运营平台按钮 */
const OperationPlatformButton = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { navigateToFirstAllowMenu } = agentPermissionHooks.useUserPermission();

  return (
    <Tooltip
      side="top"
      align="center"
      trigger={
        <div
          className={cn(
            "flex items-center px-2.5 py-1.5 bg-white text-[rgba(16,24,40,1)] rounded-md border border-[rgba(209,213,219,1)] hover:bg-gray-50 cursor-pointer",
            className
          )}
          onClick={() => navigateToFirstAllowMenu({ newWindow: true })}
        >
          <MonitorIcon className="size-5" />
        </div>
      }
      content={
        <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
          <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
            {t('agent_creation.operation_platform')}
          </span>
          <div className="absolute bottom-full right-1/3 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-black"></div>
        </div>
      }
    />
  );
};
